// 1. Core & External Libraries
import { useRoute } from '@react-navigation/native';
import { format, parseISO, startOfDay } from 'date-fns';
import { memo, useCallback, useMemo, useState } from 'react';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

// 2. Layout & shared UI
import { Scroll, Stack } from '@/components/layout';
import { Button, Text } from '@/components/ui';

// 3. Feature (guardian)
import DayTimeline from '@/features/guardian/components/family-calendar/day-timeline/day-timeline';
import WeekStrip from '@/features/guardian/components/family-calendar/day-timeline/week-strip';
import PlayerFilterChip from '@/features/guardian/components/family-calendar/player-filter-chip';
import {
  filterEventsByPlayer,
  toCalendarPlayers,
} from '@/features/guardian/components/family-calendar/screen-utils';
import { useFamilyEvents } from '@/features/guardian/hooks/use-family-events';
import { useOpenEventDetail } from '@/features/guardian/hooks/use-open-event-detail';

// 4. Hooks, strings, theme, types
import { STRINGS } from '@/constants/strings';
import { createStyles, useGuardianStackNavigation, useScaling } from '@/hooks';
import type { GuardianStackScreenProps } from '@/navigations/types';
import { colors, guardianColors, withOpacity } from '@/theme/colors';

const strings = STRINGS.guardian.kids;

/**
 * Family Calendar — Daily View (mobile only), BR-04.
 *
 * Reached by tapping a date in the Calendar screen's month grid (never from the
 * Calendar/List toggle). Shows a swipeable Mon–Sun strip over an hour-by-hour timeline
 * for the selected day, with a live "now" line on today only (BR-07) and overlapping
 * events laid out side by side.
 */
function GuardianFamilyCalendarDayScreen() {
  const { scale } = useScaling();
  const styles = useStyles();
  const insets = useSafeAreaInsets();
  const navigation = useGuardianStackNavigation();
  const route = useRoute<GuardianStackScreenProps<'GuardianFamilyCalendarDay'>['route']>();
  const { players: guardianPlayers, events } = useFamilyEvents();
  const handleEventPress = useOpenEventDetail();

  const [today] = useState(() => startOfDay(new Date()));
  const [selectedDate, setSelectedDate] = useState(() => startOfDay(parseISO(route.params.date)));
  const [selectedPlayer, setSelectedPlayer] = useState<string | null>(
    route.params.playerId ?? null,
  );

  const players = useMemo(() => toCalendarPlayers(guardianPlayers), [guardianPlayers]);
  const playerEvents = useMemo(
    () => filterEventsByPlayer(events, selectedPlayer),
    [events, selectedPlayer],
  );
  const eventDates = useMemo(() => new Set(playerEvents.map(event => event.date)), [playerEvents]);

  const selectedDateKey = format(selectedDate, 'yyyy-MM-dd');
  const dayEvents = useMemo(
    () => playerEvents.filter(event => event.date === selectedDateKey),
    [playerEvents, selectedDateKey],
  );

  const handleBack = useCallback(() => navigation.goBack(), [navigation]);

  // Tap the active chip again to clear the filter back to "all players".
  const handleSelectPlayer = useCallback(
    (playerId: string) => setSelectedPlayer(current => (current === playerId ? null : playerId)),
    [],
  );

  return (
    <Stack flex={1} backgroundColor={guardianColors['1']}>
      <Stack
        direction="row"
        align="center"
        gap={scale(8)}
        paddingTop={insets.top + scale(8)}
        paddingX={scale(20)}>
        <Button variant="ghost" onPress={handleBack} style={styles.backButton}>
          <Stack
            viewType="liquid-glass"
            align="center"
            justify="center"
            backgroundColor={withOpacity(colors['0'], 0.3)}
            overlayColor={withOpacity(colors['0'], 0.3)}
            intensity={20}
            tint="light"
            style={styles.backButtonGlass}>
            <Button.Icon
              name="IcBackBlue"
              color={guardianColors['10']}
              styleWrapper={styles.backButtonIcon}
            />
          </Stack>
        </Button>
        <Stack>
          <Text variant="Font Guardian App/Subtitle/Subtitle 12 Regular" style={styles.eyebrow}>
            {strings.calendarTitle}
          </Text>
          <Text variant="Font Guardian App/Heading/Heading 20" style={styles.monthLabel}>
            {format(selectedDate, 'MMMM yyyy')}
          </Text>
        </Stack>
      </Stack>

      <Stack direction="row" align="center" paddingBottom={scale(4)}>
        <Stack flex={1}>
          <Scroll
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.chipRow}>
            {players.map(player => (
              <PlayerFilterChip
                key={player.id}
                player={player}
                isActive={selectedPlayer === player.id}
                onPress={handleSelectPlayer}
              />
            ))}
          </Scroll>
        </Stack>
        <Stack paddingLeft={scale(8)} paddingRight={scale(20)}>
          <Text variant="Font Guardian App/Body/Body 16 Semibold" style={styles.eventCount}>
            {strings.eventCount(dayEvents.length)}
          </Text>
        </Stack>
      </Stack>

      <Stack paddingX={scale(20)}>
        <WeekStrip
          selectedDate={selectedDate}
          today={today}
          eventDates={eventDates}
          onSelectDate={setSelectedDate}
        />
      </Stack>

      <Stack align="center" paddingY={scale(12)}>
        <Text variant="Font Guardian App/Subtitle/Subtitle 14 Semibold" style={styles.dateTitle}>
          {format(selectedDate, 'EEEE - MMM d, yyyy')}
        </Text>
      </Stack>

      <DayTimeline
        date={selectedDate}
        today={today}
        events={dayEvents}
        onEventPress={handleEventPress}
        contentBottomInset={insets.bottom + scale(24)}
      />
    </Stack>
  );
}

const useStyles = createStyles(({ scale }) => ({
  backButton: {
    width: scale(40),
    height: scale(40),
    borderRadius: scale(20),
    padding: 0,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'transparent',
    shadowColor: 'transparent',
    elevation: 0,
  },
  backButtonGlass: {
    width: scale(40),
    height: scale(40),
    borderRadius: scale(20),
    paddingTop: scale(10),
    paddingRight: scale(12),
    paddingBottom: scale(10),
    paddingLeft: scale(10),
  },
  backButtonIcon: {
    marginRight: 0,
  },
  eyebrow: {
    color: guardianColors['6'],
  },
  monthLabel: {
    color: guardianColors['10'],
  },
  chipRow: {
    alignItems: 'center',
    gap: scale(10),
    // Room on every side so chip shadows spread instead of clipping at the scroll edge.
    paddingLeft: scale(20),
    paddingRight: scale(12),
    paddingVertical: scale(12),
  },
  eventCount: {
    color: guardianColors['10'],
  },
  dateTitle: {
    color: guardianColors['10'],
  },
}));

export { GuardianFamilyCalendarDayScreen };
export default memo(GuardianFamilyCalendarDayScreen);
